import { inject, Injectable } from '@angular/core';
import { AccountancyPeriodDescriptor, AnnouncementsNotificator } from '@skylab-angular-shared/business';
import { EnumTranslator, injectTranslation, RootContext } from '@skylab-angular-shared/core';
import { DateFormatPipe } from '@skylab-angular-shared/pipes';
import { DocumentsRouting } from '@skylab-angular-shared/scenes';
import { TemplateParts } from '@skylab-angular-shared/ui';
import { IconName } from '@skylab-assets/icons';
import { AccountancyPeriodDescriptorBase, AnnouncementsNotificatorBase, CompanyConfigUrlsQuery, CompanyParametersQuery, SkylabConfig } from '@skylab-shared/business';
import { DataFormats } from '@skylab-shared/constants';
import { ConsoleLogger, LocalContext, RootContextBase } from '@skylab-shared/core';
import {
	Announcement,
	AnnouncementContentKind,
	AnnouncementKind,
	BackgroundTaskKind,
	DataExchangeEmailDeliveryFailure,
	DataExchangeKind,
	DocumentEmailDeliveryFailure,
	DocumentTemplateIssuingFailure,
	DocumentTemplatesDeactivated,
	EnumTranslatorBase,
	ImportAnnouncementContent,
	InformationAnnouncementContent,
	KsefProcessingEndedAnnouncementContent,
	KsefReceivingEndedAnnouncementContent,
	KsefReceivingErrorReason,
	KsefReceivingFailedAnnouncementContent,
	NewVersionAnnouncementContent,
	PackageOfDocumentsAnnouncementContent,
	UnsettledDocumentReminderDeliveryFailure
} from '@skylab-shared/dataModel';
import { AppStates, ConfigurationParametersViews } from '@skylab-shared/scenes';
import { isNumber, transformObject } from '@skylab-shared/utils';
import { BehaviorSubject } from 'rxjs';

export interface AnnouncementItem extends Announcement {
	icon?: AnnouncementIcons;
	color?: AnnouncementColors;
	text?: string;
	canClose?: boolean;
	title?: string;

	outerUrl: string;
	outerUrlText?: string;

	template?: string;
	templateContext?: any;
}

type AnnouncementColors = 'info' | 'warning' | 'error' | 'success';
type AnnouncementIcons = Extract<IconName, 'info' | 'check-circle' | 'exclamation-triangle'>;

@Injectable({ providedIn: 'root' })
export class AnnouncementsService {
	public readonly announcements$ = new BehaviorSubject<AnnouncementItem[]>([]);

	readonly #logger = new ConsoleLogger(this.constructor.name);
	readonly #T = injectTranslation();
	readonly #announcementNotificator = inject(AnnouncementsNotificator) as AnnouncementsNotificatorBase;
	readonly #enumTranslator = inject(EnumTranslator) as EnumTranslatorBase;
	readonly #accountancyPeriodDescriptor = inject(AccountancyPeriodDescriptor) as AccountancyPeriodDescriptorBase;
	readonly #dateFormatPipe = inject(DateFormatPipe);
	readonly #localContext!: LocalContext;

	readonly #preparers = new Map<AnnouncementContentKind, (a: AnnouncementItem) => AnnouncementItem>([
		[AnnouncementContentKind.Info, a => this.#prepareInfo(a)],
		[AnnouncementContentKind.NewOnlineReceiverAvailable, a => this.#prepareNewOnlineReceiver(a)],
		[AnnouncementContentKind.FirstSteps, a => this.#prepareFirstSteps(a)],
		[AnnouncementContentKind.NewVersion, a => this.#prepareNewVersion(a)],
		[AnnouncementContentKind.BackgroundTaskEnded, a => this.#prepareBackgroundTask(a)],
		[AnnouncementContentKind.BackgroundTaskStarted, a => this.#prepareBackgroundTask(a)],
		[AnnouncementContentKind.DocumentEmailDeliveryFailure, a => this.#prepareDocumentEmailDeliveryFailure(a)],
		[AnnouncementContentKind.UnsettledDocumentReminderDeliveryFailure, a => this.#prepareUnsettledDocumentEmailDeliveryFailure(a)],
		[AnnouncementContentKind.DataExchangeEmailDeliveryFailure, a => this.#prepareDataExchangeDeliveryFailure(a)],
		[AnnouncementContentKind.DocumentTemplatesDeactivated, a => this.#prepareDocumentTemplatesDeactivated(a)],
		[AnnouncementContentKind.DocumentTemplateIssuingFailure, a => this.#prepareDocumentTemplateIssuingFailure(a)],
		[AnnouncementContentKind.KsefProcessingEnded, a => this.#prepareKsefProcessingEnded(a)],
		[AnnouncementContentKind.KsefReceivingEnded, a => this.#prepareKsefReceivingEnded(a)],
		[AnnouncementContentKind.KsefReceivingFailed, a => this.#prepareKsefReceivingFailed(a)],
		[AnnouncementContentKind.KsefReceivingStoppedDueToInsufficientFunds, a => this.#prepareKsefReceivingInsufficientFunds(a)],
		[AnnouncementContentKind.KsefSendingStoppedDueToInsufficientFunds, a => this.#prepareKsefSendingInsufficientFunds(a)],
		[AnnouncementContentKind.KsefExchangeLimitExceeded, a => this.#prepareKsefExchangeLimitExceeded(a)]
	]);

	constructor() {
		this.#localContext = (inject(RootContext) as unknown as RootContextBase).createLocalContext();
		this.#init();
	}

	public async closeAnnouncementAsync(announcement: AnnouncementItem) {
		await this.#announcementNotificator.setAsReadAsync({ id: announcement.id, contentType: announcement.contentType });
	}

	public async closeAllAnnouncementsAsync() {
		await this.#announcementNotificator.deleteAllNotificationsAsync();
	}

	async #init() {
		await Promise.all([
			this.#localContext.preload(CompanyConfigUrlsQuery),
			this.#localContext.preload(CompanyParametersQuery)
		]);

		this.#refreshAnnouncements();

		this.#announcementNotificator.addObserver(() => {
			this.#refreshAnnouncements();
		});
	}

	#refreshAnnouncements() {
		const announcements = (this.#announcementNotificator.getAnnouncements() as AnnouncementItem[])
			.map(a => this.#setAnnouncementContentType(a))
			.filter(a => this.#filterUnusedAnnouncementsInPopup(a));

		const prepared = this.#sortAnnouncements(announcements.map(raw =>
			transformObject<AnnouncementItem>(
				a => this.#assignCanClose(a),
				a => this.#assignAppearance(a),
				a => this.#recomposeText(a),
			)(raw)
		));

		this.announcements$.next(prepared);
	}

	#filterUnusedAnnouncementsInPopup(announcement: AnnouncementItem) {
		const unusedAnnouncements: AnnouncementContentKind[] = [
			AnnouncementContentKind.ExpiringLicenses,
			AnnouncementContentKind.BackgroundDataPreparationPending
		];
		return announcement.contentType !== undefined && !unusedAnnouncements.includes(announcement.contentType);
	}

	#setAnnouncementContentType(announcement: AnnouncementItem): AnnouncementItem {
		let contentType: AnnouncementContentKind | undefined = undefined;
		try {
			const parsed = JSON.parse(announcement.content);
			if (parsed && typeof parsed === 'object' && 'Kind' in parsed) {
				contentType = AnnouncementContentKind[parsed.Kind as keyof typeof AnnouncementContentKind];
			}
		} catch (err) {
			this.#logger.error(err);
		}
		return { ...announcement, contentType };
	}

	#assignCanClose(announcement: AnnouncementItem): AnnouncementItem {
		return {
			...announcement,
			canClose: true
		};
	}

	#assignAppearance(announcement: AnnouncementItem): AnnouncementItem {
		return {
			...announcement,
			color: this.#getColorByKind(announcement.kind),
			icon: this.#getIconByKind(announcement.kind),
			title: this.#T(`scenes.announcements.kinds.${this.#getColorByKind(announcement.kind) ?? 'info'}`),
		};
	}

	#getColorByKind(kind: AnnouncementKind): AnnouncementColors | undefined {
		const colors = {
			[AnnouncementKind.Info]: 'info',
			[AnnouncementKind.Success]: 'success',
			[AnnouncementKind.Warning]: 'warning',
			[AnnouncementKind.Error]: 'error',
		} satisfies Record<AnnouncementKind, AnnouncementColors>;

		return colors[kind];
	}

	#getIconByKind(kind: AnnouncementKind): AnnouncementIcons | undefined {
		const icons = {
			[AnnouncementKind.Info]: 'info',
			[AnnouncementKind.Success]: 'check-circle',
			[AnnouncementKind.Warning]: 'exclamation-triangle',
			[AnnouncementKind.Error]: 'exclamation-triangle',
		} satisfies Record<AnnouncementKind, AnnouncementIcons>;

		return icons[kind];
	}

	#prepareInfo(announcement: AnnouncementItem) {
		const parsedAnnouncement: InformationAnnouncementContent = JSON.parse(announcement.content);
		return {
			...announcement,
			text: parsedAnnouncement.Info,
			template: TemplateParts.getTemplate(parsedAnnouncement.Info)
		} satisfies AnnouncementItem;
	}

	#prepareNewOnlineReceiver(announcement: AnnouncementItem) {
		const { accountingOnlineReceiver } = this.#localContext.getPreloaded(CompanyParametersQuery);

		const text = this.#T('scenes.announcements.online-exchange-newer-receiver');

		return {
			...announcement,
			text,
			templateContext: {
				text: `${text} -`,
				linkText: this.#T('scenes.announcements.online-exchange-newer-receiver-link'),
				route: {
					url: AppStates.configurationParameters.url,
					params: {
						view: ConfigurationParametersViews.OnlineReceivers,
						id: accountingOnlineReceiver?.id || 0
					}
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareFirstSteps(announcement: AnnouncementItem) {
		return {
			...announcement,
			text: this.#T('scenes.announcements.first-steps'),
			outerUrlText: this.#T('scenes.announcements.first-steps-link'),
			outerUrl: SkylabConfig.firstStepsUrl
		} satisfies AnnouncementItem;
	}

	#prepareNewVersion(announcement: AnnouncementItem) {
		const parsedAnnouncement: NewVersionAnnouncementContent = JSON.parse(announcement.content);

		return {
			...announcement,
			text: `${this.#dateFormatPipe.transform(new Date(parsedAnnouncement.Date), DataFormats.date.dayMonthYear)} ${this.#T('scenes.announcements.publish')}`,
			outerUrlText: this.#T('scenes.announcements.publish-link'),
			outerUrl: parsedAnnouncement.Link,
		} satisfies AnnouncementItem;
	}

	#prepareBackgroundTask(announcement: AnnouncementItem) {
		const parsed: ImportAnnouncementContent | PackageOfDocumentsAnnouncementContent = JSON.parse(announcement.content);

		if (this.#isImportAnnouncement(parsed)) return this.#prepareImportTask(announcement, parsed);

		return this.#preparePackageOfDocumentsTask(announcement, parsed);
	}

	#isImportAnnouncement(parsed: ImportAnnouncementContent | PackageOfDocumentsAnnouncementContent): parsed is ImportAnnouncementContent {
		return [BackgroundTaskKind.ClientsImport, BackgroundTaskKind.ProductsImport].includes(parsed.Task);
	}

	#prepareImportTask(announcement: AnnouncementItem, parsed: ImportAnnouncementContent) {
		if (announcement.kind === AnnouncementKind.Error) {
			return { ...announcement, text: this.#T('scenes.announcements.imports.incorrect-data-format-error') };
		}

		const taskLabel = `${this.#enumTranslator.getLabeledEnumValue(AnnouncementContentKind, parsed.Kind).label}${this.#enumTranslator.getLabeledEnumValue(BackgroundTaskKind, BackgroundTaskKind[parsed.Task]).label}`;

		if (parsed.Imported == null) {
			return { ...announcement, text: taskLabel };
		}

		return {
			...announcement,
			text: taskLabel,
			templateContext: {
				text: `${taskLabel} (${parsed.Imported}) -`,
				linkText: this.#T('scenes.announcements.imports.show-summary'),
				route: {
					url: AppStates.importSummary.url,
					params: { id: parsed.TaskId }
				}
			}
		} satisfies AnnouncementItem;
	}

	#preparePackageOfDocumentsTask(announcement: AnnouncementItem, parsed: PackageOfDocumentsAnnouncementContent) {
		const dateDescription = this.#accountancyPeriodDescriptor.describe({
			from: new Date(parsed.DateRangeFrom || ''),
			to: new Date(parsed.DateRangeTo || ''),
			kind: DataExchangeKind.PackageOfDocuments
		});

		const kindToKey: Partial<Record<AnnouncementKind, string>> = {
			[AnnouncementKind.Error]: 'error',
			[AnnouncementKind.Success]: 'success',
		};

		const key = kindToKey[announcement.kind] ?? 'info';
		const text = this.#T(`scenes.announcements.package-of-documents.${key}`, { dateRange: dateDescription });

		return { ...announcement, text } satisfies AnnouncementItem;
	}

	#prepareDocumentEmailDeliveryFailure(announcement: AnnouncementItem) {
		const parsedAnnouncement: DocumentEmailDeliveryFailure = JSON.parse(announcement.content);
		const email = parsedAnnouncement.Recipient;

		return {
			...announcement,
			text: this.#T('scenes.announcements.document-email-delivery-failure.email-delivery-failure-without-number', { email }),
			templateContext: {
				number: parsedAnnouncement.DocumentNumber,
				textNoNumber: this.#T('scenes.announcements.document-email-delivery-failure.email-delivery-failure-without-number', { email }),
				textBefore: this.#T('scenes.announcements.document-email-delivery-failure.email-delivery-failure'),
				textAfter: this.#T('scenes.announcements.document-email-delivery-failure.to-the-email-address', { email }),
				route: {
					url: DocumentsRouting.getEditState(parsedAnnouncement.DocumentKind).url,
					params: { id: parsedAnnouncement.DocumentId }
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareUnsettledDocumentEmailDeliveryFailure(announcement: AnnouncementItem) {
		const parsedAnnouncement: UnsettledDocumentReminderDeliveryFailure = JSON.parse(announcement.content);
		const email = parsedAnnouncement.Recipient;

		return {
			...announcement,
			text: this.#T('scenes.announcements.unsettled-document-reminder-delivery-failure.email-delivery-failure-without-number', { email }),
			templateContext: {
				number: parsedAnnouncement.DocumentNumber,
				textNoNumber: this.#T('scenes.announcements.unsettled-document-reminder-delivery-failure.email-delivery-failure-without-number', { email }),
				textBefore: this.#T('scenes.announcements.unsettled-document-reminder-email-delivery-failure.email-delivery-failure'),
				textAfter: this.#T('scenes.announcements.unsettled-document-reminder-email-delivery-failure.to-the-email-address', { email }),
				route: {
					url: DocumentsRouting.getEditState(parsedAnnouncement.DocumentKind).url,
					params: { id: parsedAnnouncement.DocumentId }
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareDataExchangeDeliveryFailure(announcement: AnnouncementItem) {
		const parsed: DataExchangeEmailDeliveryFailure = JSON.parse(announcement.content);

		const text = !parsed.DataExchangeKind
			? this.#T('scenes.announcements.data-exchange-email-delivery-failure.email-delivery-failure-without-details', { email: parsed.Recipient })
			: this.#T('scenes.announcements.data-exchange-email-delivery-failure.email-delivery-failure', {
				kind: parsed.DataExchangeKind,
				dateRange: this.#accountancyPeriodDescriptor.describe({
					from: new Date(parsed.DateRangeFrom || ''),
					to: new Date(parsed.DateRangeTo || ''),
					kind: parsed.DataExchangeKind!
				}),
				email: parsed.Recipient
			});

		return {
			...announcement,
			text,
		} satisfies AnnouncementItem;
	}

	#prepareDocumentTemplatesDeactivated(announcement: AnnouncementItem) {
		const parsedAnnouncement: DocumentTemplatesDeactivated = JSON.parse(announcement.content);

		return {
			...announcement,
			text: this.#T('scenes.announcements.document-templates-deactivated'),
			templateContext: {
				text: this.#T('scenes.announcements.document-templates-deactivated'),
				linkText: this.#T('scenes.announcements.document-templates-deactivated-link'),
				route: {
					url: AppStates.salePattern.url,
					params: { id: parsedAnnouncement.DocumentTemplatesIds }
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareDocumentTemplateIssuingFailure(announcement: AnnouncementItem) {
		const parsedAnnouncement: DocumentTemplateIssuingFailure = JSON.parse(announcement.content);

		return {
			...announcement,
			text: this.#T('scenes.announcements.document-template-issuing-failure.content'),
			templateContext: {
				text: this.#T('scenes.announcements.document-template-issuing-failure.content'),
				linkText: parsedAnnouncement.TemplateName,
				route: {
					url: AppStates.editSalePattern.url,
					params: { id: parsedAnnouncement.TemplateId }
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareKsefProcessingEnded(announcement: AnnouncementItem) {
		const parsedAnnouncement: KsefProcessingEndedAnnouncementContent = JSON.parse(announcement.content);

		const success = announcement.kind === AnnouncementKind.Success;
		const text = success
			? this.#T('scenes.announcements.document-ksef-processing-ended.success')
			: this.#T('scenes.announcements.document-ksef-processing-ended.failure');

		return {
			...announcement,
			text,
			templateContext: {
				text: `${text} -`,
				linkText: parsedAnnouncement.DocumentNumber,
				route: {
					url: DocumentsRouting.getEditState(parsedAnnouncement.DocumentKind).url,
					params: { id: parsedAnnouncement.DocumentId }
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareKsefReceivingEnded(announcement: AnnouncementItem) {
		const parsedAnnouncement: KsefReceivingEndedAnnouncementContent = JSON.parse(announcement.content);

		const count = isNumber(parsedAnnouncement.DownloadedXmlCount) ? ` (${parsedAnnouncement.DownloadedXmlCount})` : '';
		const text = `${this.#T('scenes.announcements.ksef-receiving-ended.success.text')}${count}`;

		return {
			...announcement,
			text,
			templateContext: {
				text: `${text} -`,
				linkText: this.#T('scenes.announcements.ksef-receiving-ended.success.link'),
				route: {
					url: AppStates.eInvoicesReceived.url
				}
			}
		} satisfies AnnouncementItem;
	}

	#prepareKsefReceivingFailed(announcement: AnnouncementItem) {
		const parsedAnnouncement: KsefReceivingFailedAnnouncementContent = JSON.parse(announcement.content);
		const errorReason = parsedAnnouncement.ErrorReason as string;

		if (errorReason === KsefReceivingErrorReason.InsufficientWalletUnits || errorReason === 'InsufficientWallet') {
			return { ...announcement, text: this.#T('scenes.announcements.ksef-receiving-stopped-insufficient-funds.text.single') } satisfies AnnouncementItem;
		}

		let text = this.#T('scenes.announcements.ksef-receiving-ended.failure.basic');
		if (parsedAnnouncement.ErrorReason === KsefReceivingErrorReason.LackOfPermissions) text += ` ${this.#T('scenes.announcements.ksef-receiving-ended.failure.unauthorized')}`;

		return { ...announcement, text } satisfies AnnouncementItem;
	}

	#prepareKsefReceivingInsufficientFunds(announcement: AnnouncementItem) {
		const text = this.#T('scenes.announcements.ksef-receiving-stopped-insufficient-funds.text.multi');
		return { ...announcement, text } satisfies AnnouncementItem;
	}

	#prepareKsefSendingInsufficientFunds(announcement: AnnouncementItem) {
		const text = this.#T('scenes.announcements.ksef-sending-stopped-insufficient-funds.text');
		return { ...announcement, text } satisfies AnnouncementItem;
	}

	#prepareKsefExchangeLimitExceeded(announcement: AnnouncementItem) {
		const text = this.#T('scenes.announcements.ksef-exchange-limit-exceeded.text');
		return { ...announcement, text } satisfies AnnouncementItem;
	}

	#recomposeText(announcement: AnnouncementItem) {
		const preparer = announcement.contentType ? this.#preparers.get(announcement.contentType) : undefined;

		if (preparer) {
			try {
				return preparer(announcement);
			} catch (ex) {
				this.#logger.error(ex, 'Błąd podczas preparowania:');
			}
		} else {
			this.#logger.error(`Nieznany rodzaj komunikatu: ${announcement.contentType}`);
		}

		return {
			...announcement,
			text: this.#T('scenes.announcements.incorrect-announcement-format-error')
		} satisfies AnnouncementItem;
	}

	#sortAnnouncements(announcements: AnnouncementItem[]) {
		return announcements.sort((a, b) => Number(!!a.canClose) - Number(!!b.canClose));
	}
}
